import { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/App";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Search, X } from "lucide-react";
import { errorText } from "@/lib/calendar";
import { AccountRow } from "./AccountRow";
import { AccountActionDialog } from "./AccountActionDialog";

export const AccountsPanel = ({ filter, onClearFilter }) => {
  const [accounts, setAccounts] = useState(null);
  const [q, setQ] = useState("");
  const [pending, setPending] = useState({ action: null, account: null });

  const load = useCallback(async (search) => {
    try {
      const params = { q: search || undefined, org_id: filter?.id || undefined };
      setAccounts((await apiClient.get("/owner/accounts", { params })).data);
    } catch (e) {
      toast.error(errorText(e, "Could not load accounts"));
    }
  }, [filter]);

  useEffect(() => {
    const t = setTimeout(() => load(q), 300);
    return () => clearTimeout(t);
  }, [q, load]);

  const done = () => { setPending({ action: null, account: null }); load(q); };

  return (
    <div data-testid="owner-accounts-panel">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email" className="pl-9 bg-white" data-testid="owner-accounts-search" />
        </div>
        {filter && (
          <Button variant="outline" size="sm" onClick={onClearFilter} className="self-start bg-white" data-testid="owner-accounts-clear-filter">
            {filter.name} <X className="w-3.5 h-3.5 ml-2" />
          </Button>
        )}
        <span className="text-sm text-slate-400 sm:ml-auto" data-testid="owner-accounts-count">{accounts ? `${accounts.length} accounts` : "Loading…"}</span>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left">Account</th><th className="px-4 py-3 text-left">Role</th>
              <th className="px-4 py-3 text-left">Company</th><th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Created</th><th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(accounts || []).map((a) => <AccountRow key={a.id} a={a} onAction={(action, account) => setPending({ action, account })} />)}
          </tbody>
        </table>
        {accounts?.length === 0 && <p className="text-center text-slate-400 py-10" data-testid="owner-accounts-empty">No accounts match.</p>}
      </div>
      <AccountActionDialog action={pending.action} account={pending.account} onClose={() => setPending({ action: null, account: null })} onDone={done} />
    </div>
  );
};
